/* eslint-disable camelcase, react/no-danger */
import React, {useEffect, useState} from 'react'
import {css} from '@emotion/core'
import PropTypes from 'prop-types'
import {getTheme, getThemeStyles} from '../util/theme'
import {getLanguageCode} from '../util/locale'
import {createMarkup} from '../graphql/datoCmsDataFactory'

import Layout from '../components/Layout'
import {stripTags} from '../util/html'

import supportPrivacyPolicyData from '../translations/support-privacy-policy.json'

const buildPrivacyPolicyBody = (sections) => {
    const mklis = (entries) => entries.map((li, idx) => <li key={idx} dangerouslySetInnerHTML={createMarkup(li)} />)
    return sections.map((section) => (
        <>
            <h2>{section.heading}</h2>
            {section.contents.map((content) => {
                if (typeof content === 'string') {
                    return <p dangerouslySetInnerHTML={createMarkup(content)} />
                }
                const [[tag, tc]] = Object.entries(content)
                switch (tag) {
                    case 'ol':
                        return <ol>{mklis(tc)}</ol>
                    case 'ul':
                        return <ul>{mklis(tc)}</ul>
                    default:
                        return createMarkup(`<${tag}>${tc}</${tag}>`)
                }
            })}
        </>
    ))
}

const buildMetaDescription = (sections) =>
    sections
        .map(
            (section) =>
                section.heading +
                section.contents
                    .map((content) => stripTags(typeof content === 'string' ? content : Object.values(content).join()))
                    .join(),
        )
        .join()

const SupportPrivacyPolicy = ({languageCode}) => {
    const lang = languageCode || getLanguageCode()

    const {supportPrivacyPolicyTitle, supportPrivacyPolicySections} = supportPrivacyPolicyData[lang]

    const [theme, setTheme] = useState('light')
    const [themeStyles, setThemeStyles] = useState({})

    useEffect(() => {
        const t = getTheme()
        const tStyles = getThemeStyles()
        setTheme(t)
        setThemeStyles(tStyles)
    }, [themeStyles])

    const metaTitle = supportPrivacyPolicyTitle
    const metaDescription = buildMetaDescription(supportPrivacyPolicySections)

    return (
        <Layout
            title={metaTitle}
            description={metaDescription}
            lang={lang}
            canonicalPath="/support-privacy-policy/"
            themeStyles={themeStyles}
            theme={theme}
        >
            <section
                className="main-section"
                css={css`
                    margin-top: -1px;
                    background: ${themeStyles.lighterBackgroundColor};
                `}
            >
                <div
                    className="container"
                    css={css`
                        margin: 88px auto 0;
                        position: relative;
                        overflow: hidden;
                        padding: 72px 1rem 8rem;
                        max-width: 1000px;
                        h1,
                        h2,
                        p,
                        b,
                        li {
                            color: ${themeStyles.primaryTextColor};
                        }
                        h1 {
                            margin: 0 0 56px;
                            font-size: 40px;
                            font-weight: 500;
                            @media (max-width: 900px) {
                                font-size: 30px;
                                margin: 0 0 45px;
                            }
                        }
                        h2 {
                            font-weight: 500;
                            font-size: 24px;
                            margin: 0 0 13px;
                            @media (max-width: 900px) {
                                font-size: 20px;
                            }
                        }
                        p,
                        li {
                            font-size: 16px;
                            line-height: 24px;
                        }
                        p {
                            margin: 0 0 26px;
                        }
                        li {
                            margin: 0 0 13px;
                        }
                        a {
                            color: ${themeStyles.linkColor};
                        }
                        @media (max-width: 900px) {
                            padding: 4rem 2rem 3rem;
                        }
                    `}
                >
                    <h1>{supportPrivacyPolicyTitle}</h1>
                    <div>{buildPrivacyPolicyBody(supportPrivacyPolicySections)}</div>
                </div>
            </section>
        </Layout>
    )
}

SupportPrivacyPolicy.propTypes = {
    languageCode: PropTypes.string,
}

export default SupportPrivacyPolicy
